import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api.js';

/**
 * useSettings — general settings hooks.
 *
 * Consumes these endpoints (Randy's backend):
 *   GET  /api/dashboard/settings/organisation
 *   PUT  /api/dashboard/settings/organisation     — rename, change slug, contact email
 *   POST /api/dashboard/settings/password         — change password
 *   POST /api/dashboard/settings/email/change     — change email (triggers verification)
 *   DELETE /api/dashboard/settings/account        — cascade delete tenant + staff + scans
 *
 * Organisation GET returns: {
 *   name, slug, public_contact_email, created_at,
 *   current_slug_expires_at  // for old-slug redirect grace period
 * }
 */
export function useOrganisation() {
  const [state, setState] = useState({ data: null, loading: true, error: null });

  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.get('/api/dashboard/settings/organisation');
      setState({ data: data.organisation, loading: false, error: null });
    } catch (e) {
      setState({ data: null, loading: false, error: e });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refresh: load };
}

export function useSettingsMutations() {
  const updateOrganisation = useCallback(async (partial) => {
    const data = await api.put('/api/dashboard/settings/organisation', partial);
    return data.organisation;
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    return api.post('/api/dashboard/settings/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }, []);

  const requestEmailChange = useCallback(async (newEmail, currentPassword) => {
    return api.post('/api/dashboard/settings/email/change', {
      new_email: newEmail,
      current_password: currentPassword,
    });
  }, []);

  const deleteAccount = useCallback(async (currentPassword, confirmationText) => {
    return api.del('/api/dashboard/settings/account', {
      current_password: currentPassword,
      confirmation: confirmationText,
    });
  }, []);

  return { updateOrganisation, changePassword, requestEmailChange, deleteAccount };
}

/* ─────────────────────────────────────────────────────────── */
/*  Team                                                        */
/* ─────────────────────────────────────────────────────────── */

/**
 * useTeam — list + mutate the tenant's admin team.
 *
 * Endpoints:
 *   GET    /api/dashboard/admins                   — list admins + pending invitations
 *   POST   /api/dashboard/admins/invite            — send invitation email
 *   DELETE /api/dashboard/admins/:id               — remove admin (cannot remove self)
 *   DELETE /api/dashboard/admins/invitations/:id   — revoke a pending invitation
 *   PUT    /api/dashboard/admins/:id/role          — change role (owner/member)
 *
 * GET returns: {
 *   admins:      [{ id, email, role, last_login_at, is_current }],
 *   invitations: [{ id, email, role, invited_at, expires_at, invited_by_email }]
 * }
 */
export function useTeam() {
  const [state, setState] = useState({
    admins: [],
    invitations: [],
    loading: true,
    error: null,
  });

  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.get('/api/dashboard/admins');
      setState({
        admins: data.admins || [],
        invitations: data.invitations || [],
        loading: false,
        error: null,
      });
    } catch (e) {
      setState({ admins: [], invitations: [], loading: false, error: e });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refresh: load };
}

export function useTeamMutations() {
  const inviteAdmin = useCallback(async (email, role) => {
    const data = await api.post('/api/dashboard/admins/invite', { email, role });
    return data.invitation;
  }, []);

  const removeAdmin = useCallback(async (id) => {
    return api.del(`/api/dashboard/admins/${id}`);
  }, []);

  const revokeInvitation = useCallback(async (id) => {
    return api.del(`/api/dashboard/admins/invitations/${id}`);
  }, []);

  const changeRole = useCallback(async (id, role) => {
    const data = await api.put(`/api/dashboard/admins/${id}/role`, { role });
    return data.admin;
  }, []);

  return { inviteAdmin, removeAdmin, revokeInvitation, changeRole };
}

/* ─────────────────────────────────────────────────────────── */
/*  Billing                                                     */
/* ─────────────────────────────────────────────────────────── */

/**
 * useBilling — current subscription + payment method + trial state.
 *
 * Endpoint: GET /api/dashboard/billing
 *
 * Returns: {
 *   plan: 'starter'|'standard'|'professional'|'founder',
 *   status: 'trialing'|'active'|'past_due'|'suspended'|'cancelled'|'founder',
 *   trial: { days_left, expired, ends_at } | null,
 *   current_period_start, current_period_end,
 *   cancel_at: ISO | null,
 *   payment_method: { brand, last4, exp_month, exp_year } | null,
 *   active_staff_count: 0,
 *   plan_max_staff: 10,
 *   next_invoice: { amount_due_pence, due_date } | null
 * }
 */
export function useBilling() {
  const [state, setState] = useState({ data: null, loading: true, error: null });

  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await api.get('/api/dashboard/billing');
      setState({ data, loading: false, error: null });
    } catch (e) {
      setState({ data: null, loading: false, error: e });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refresh: load };
}

export function useBillingMutations() {
  /**
   * Change plan — upgrades prorate immediately, downgrades also
   * immediate with pro-rated refund (per Jez's billing rules).
   * Backend handles the Stripe subscription update.
   */
  const changePlan = useCallback(async (planKey) => {
    const data = await api.post('/api/dashboard/billing/plan', { plan: planKey });
    return data;
  }, []);

  /**
   * Preview a plan change — show the user what they'll pay / be refunded
   * BEFORE they confirm. Endpoint returns the prorated amount so the UI
   * can say "You'll be charged £2.45 today" or "You'll be refunded £1.20".
   */
  const previewPlanChange = useCallback(async (planKey) => {
    const data = await api.post('/api/dashboard/billing/plan/preview', { plan: planKey });
    return data; // { amount_due_pence, amount_refund_pence, proration_date }
  }, []);

  /**
   * Cancel subscription — immediate per Jez's rules, with pro-rated refund
   * for unused days.
   */
  const cancelSubscription = useCallback(async () => {
    return api.post('/api/dashboard/billing/cancel', {});
  }, []);

  /**
   * Resume a cancelled subscription — only works if the cancellation
   * hasn't yet taken effect (i.e. same billing period, some legacy
   * state we probably won't hit often since we cancel immediately, but
   * useful for the "undo" button right after cancellation).
   */
  const resumeSubscription = useCallback(async () => {
    return api.post('/api/dashboard/billing/resume', {});
  }, []);

  /**
   * Start a SetupIntent for updating the payment method.
   * Returns client_secret for use in Stripe Elements.
   */
  const startPaymentMethodUpdate = useCallback(async () => {
    const data = await api.post('/api/dashboard/billing/payment-method/setup', {});
    return data.client_secret;
  }, []);

  return {
    changePlan,
    previewPlanChange,
    cancelSubscription,
    resumeSubscription,
    startPaymentMethodUpdate,
  };
}
